import React from 'react';
import dayjs from 'dayjs';
import { AlertTriangle, Check } from 'lucide-react';
import { CopyToClipboardButton } from '@plerionhq/design-system';
import type { FactCheckVerification } from './FactCheckLookupContext';

import { useTranslation } from 'react-i18next';

function formatLocalDateTime(checkedAt: string): string {
  try {
    const d = dayjs(checkedAt);
    return d.isValid() ? d.format('MMM D, YYYY, h:mm:ss A') : '';
  } catch {
    return '';
  }
}

function getFactualityLabel(factuality?: string): string {
  return factuality === 'true'
    ? 'Factual'
    : factuality === 'false'
      ? 'Non-factual'
      : 'Unknown';
}

function formatXml(sourceXml: string): string {
  try {
    const xmlDoc = new DOMParser().parseFromString(
      sourceXml,
      'application/xml',
    );
    const xsltDoc = new DOMParser().parseFromString(
      [
        '<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform">',
        '  <xsl:strip-space elements="*"/>',
        '  <xsl:template match="para[content-style][not(text())]">',
        '    <xsl:value-of select="normalize-space(.)"/>',
        '  </xsl:template>',
        '  <xsl:template match="node()|@*">',
        '    <xsl:copy><xsl:apply-templates select="node()|@*"/></xsl:copy>',
        '  </xsl:template>',
        '  <xsl:output indent="yes"/>',
        '</xsl:stylesheet>',
      ].join('\n'),
      'application/xml',
    );
    const xsltProcessor = new XSLTProcessor();
    xsltProcessor.importStylesheet(xsltDoc);
    const resultDoc = xsltProcessor.transformToDocument(xmlDoc);
    return new XMLSerializer().serializeToString(resultDoc);
  } catch {
    return sourceXml;
  }
}

function isFindingFailed(status?: string): boolean {
  return status?.toUpperCase() === 'FAILED';
}

function isFindingPassed(status?: string): boolean {
  return status?.toUpperCase() === 'PASSED';
}

function FactualityBadge({
  factuality,
  findingStatus,
}: {
  factuality?: string;
  findingStatus?: string;
}) {
  const { t } = useTranslation('pleri/chat');
  const label = getFactualityLabel(factuality);
  const isFactual = factuality === 'true';
  const failed = isFindingFailed(findingStatus);
  const passed = isFindingPassed(findingStatus);

  if (isFactual && failed) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-sm font-semibold text-amber-800 dark:bg-amber-900"
        aria-label={t('factualityConcern', { label })}
      >
        <AlertTriangle
          className="h-4 w-4 shrink-0 text-amber-700"
          strokeWidth={2.5}
          aria-hidden
        />
        {label}
      </span>
    );
  }
  if (isFactual && passed) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-sm font-semibold text-green-800 dark:bg-green-900"
        aria-label={t('factuality', { label })}
      >
        <Check
          className="h-4 w-4 shrink-0 text-green-700"
          strokeWidth={2.5}
          aria-hidden
        />
        {label}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-sm font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300"
      aria-label={t('factuality', { label })}
    >
      {label}
    </span>
  );
}

interface FactCheckViewProps {
  verification: FactCheckVerification;
  findingStatus?: string;
}

export const FactCheckView: React.FC<FactCheckViewProps> = ({
  verification,
  findingStatus,
}) => {
  const { t } = useTranslation('pleri/chat');
  if (!verification || typeof verification !== 'object') {
    return (
      <p className="my-4 text-sm text-gray-500 dark:text-gray-400">
        {t('noFactCheckingDetailsAvailableForThis')}
      </p>
    );
  }

  let hasProof = false;
  let hasCheckedAt = false;
  let hasFactuality = false;
  try {
    hasProof =
      verification.proof?.environment === 'linux-shell' &&
      Array.isArray(verification.proof?.actions) &&
      verification.proof.actions.length > 0;
    hasCheckedAt =
      typeof verification.checkedAt === 'string' &&
      verification.checkedAt.trim() !== '';
    hasFactuality =
      typeof verification.factuality === 'string' &&
      verification.factuality.trim() !== '';
  } catch {
    // If any property access throws (e.g. proxy), show minimal fallback
    return (
      <p className="my-4 text-sm text-gray-500 dark:text-gray-400">
        {t('noFactCheckingDetailsAvailableForThis')}
      </p>
    );
  }

  const hasAnyMeta = hasProof || hasCheckedAt || hasFactuality;

  if (!hasAnyMeta) {
    return (
      <p className="my-4 text-sm text-gray-500 dark:text-gray-400">
        {t('noFactCheckingDetailsAvailableForThis')}
      </p>
    );
  }

  return (
    <div className="my-4 overflow-hidden bg-white text-sm shadow-sm dark:bg-gray-900">
      {/* Fact-check card */}
      {(hasCheckedAt || hasFactuality) && (
        <div>
          {(hasFactuality || hasCheckedAt) && (
            <div className="flex w-full items-center justify-between gap-3 bg-white py-3 dark:bg-gray-900">
              {hasFactuality && (
                <FactualityBadge
                  factuality={verification.factuality}
                  findingStatus={findingStatus}
                />
              )}
              {hasCheckedAt && (
                <div className="shrink-0 text-right text-xs text-gray-500 italic dark:text-gray-400">
                  {formatLocalDateTime(verification.checkedAt!)}
                </div>
              )}
            </div>
          )}
          {verification.reason && !hasProof && (
            <div className="py-2">
              <div className="truncate text-gray-600 dark:text-gray-400">
                {t('reason', { reason: verification.reason })}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Proof section – light theme only */}
      {hasProof && (
        <div className="flex flex-col overflow-hidden">
          <div className="flex flex-col gap-3 overflow-y-auto bg-white dark:bg-gray-900">
            {verification.proof?.actions?.map((action, index) => {
              if (!action || typeof action !== 'object') {
                return null;
              }
              const command =
                typeof action.command === 'string' ? action.command : '';
              const description =
                typeof action.description === 'string'
                  ? action.description.trim()
                  : '';
              const output = action.output;
              const outputValue =
                output &&
                typeof output === 'object' &&
                typeof output.value === 'string'
                  ? output.value
                  : '';
              const outputFormat =
                output && typeof output === 'object'
                  ? output.format
                  : undefined;
              const hasOutput = outputValue.length > 0;
              return (
                <div
                  key={index}
                  className="flex flex-col gap-2 bg-gray-50/80 p-3 font-mono text-gray-800 dark:bg-gray-800/80 dark:text-gray-200"
                >
                  <div className="flex flex-row items-center gap-2">
                    <pre className="m-0 w-4 shrink-0 overflow-hidden font-semibold text-nowrap text-gray-600 dark:text-gray-400">
                      $
                    </pre>
                    <pre className="m-0 min-w-0 flex-1 text-sm break-all text-gray-800 dark:text-gray-200">
                      {command}
                    </pre>
                    {typeof navigator !== 'undefined' &&
                      navigator?.clipboard && (
                        <CopyToClipboardButton
                          text={command}
                          className="-mt-0.5 h-6 w-6 shrink-0 p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-gray-300"
                        />
                      )}
                  </div>
                  {description && (
                    <pre className="m-0 pl-6 text-xs text-gray-600 dark:text-gray-400">
                      # {description}
                    </pre>
                  )}
                  {hasOutput && (
                    <pre className="mt-3 pl-6 text-xs break-words whitespace-pre-wrap text-gray-600 dark:text-gray-400">
                      {outputFormat === 'xml'
                        ? formatXml(outputValue)
                        : outputValue}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
